import { PASSPORT, PASSPORT_CLIENT, withAuthorization, withAuthorizationAndVerification } from 'src/remotes'
import { ServerResponseError } from 'src/errors'
import { omitBy, isNil } from 'lodash'
import AsyncLock from 'async-lock'
import assert from 'assert'
import moment from 'moment'
import { TokenExpiredError } from '@chronobank/passport.client'
import { ErrorCodes } from 'src/constants'
import {
  ClientScopeModel,
  ClientModel,
  AuthContext
} from 'src/models'

const LOCK_REQUIRE_TOKEN = 'requireTokenLock'
const requireTokenLock = new AsyncLock({ domainReentrant: true })

const MIN_SECONDS_BEFORE_REFRESH = 20
const MIN_SECONDS_BEFORE_WARN = 40

export const CONTEXT_UPDATE = 'context/update'
export const DETAILS_UPDATE = 'details/update'
export const USER_UPDATE = 'user/update'
export const TOKENS_UPDATE = 'token/update'
export const TOKENS_CLEAR = 'token/clear'
export const RECOVER_TOKEN_UPDATE = 'token/recover/update'
export const FORGOT_PASSWORD_TOKEN_UPDATE = 'token/forgot_password/update'

export default () => ({
  namespaced: true,
  state: {
    context: null,
    details: null,
    user: null,
    token: null,
    refreshToken: null,
    recoverToken: null,
    forgotPasswordToken: null
  },
  mutations: {
    [CONTEXT_UPDATE] (state, context) {
      state.context = context
    },
    [DETAILS_UPDATE] (state, context) {
      state.details = context
    },
    [USER_UPDATE] (state, user) {
      state.user = user
    },
    [TOKENS_UPDATE] (state, { token, refreshToken }) {
      state.token = token
      state.refreshToken = refreshToken
    },
    [TOKENS_CLEAR] (state) {
      state.token = null
      state.refreshToken = null
    },
    [RECOVER_TOKEN_UPDATE] (state, recoverToken) {
      state.recoverToken = recoverToken
    },
    [FORGOT_PASSWORD_TOKEN_UPDATE] (state, forgotPasswordToken) {
      state.forgotPasswordToken = forgotPasswordToken
    }
  },
  getters: {
    context: state => state.context,
    details: state => state.details,
    user: state => state.user,
    token: state => state.token,
    defaultVerificator: state => {
      // default is APP 2fa
      const verificators = state.user.verificators
      if (verificators == null || verificators.length === 0) {
        return null
      }

      const appVerificators = verificators.filter(e => e.type === 'APP')
      if (appVerificators.length === 0) {
        return null
      }
      return verificators[0].type
    },
    getStrategyByCallbackUrl: state => callbackURL => {
      if (state.details && state.details.strategies) {
        return state.details.strategies.find(e => e.callbackURL === callbackURL)
      }
      return []
    }
  },
  actions: {
    async fetchClient (_, { clientId }) {
      try {
        const { data } = await PASSPORT.get(`/api/v1/auth/client`, {
          params: {
            clientId
          }
        })

        return ClientModel.fromJs(data)
      } catch (e) {
        return null
      }
    },
    async saveContext ({ commit }, { client, redirectUrl, state, refcode, codeChallenge }) {
      const context = AuthContext.fromJs({
        client,
        redirectUrl,
        state,
        refcode,
        codeChallenge
      })

      commit(CONTEXT_UPDATE, context)
    },
    async restore ({ dispatch }) {
      await dispatch('restoreUser')
    },
    async fetchUser (_, { token }) {
      const { data } = await PASSPORT.get('/api/v1/security/me', withAuthorization(token))
      return data
    },
    async restoreUser ({ state, commit, dispatch }) {
      if (state.token != null) {
        try {
          const token = await dispatch('requireToken')
          const user = await dispatch('fetchUser', { token })
          commit(USER_UPDATE, Object.freeze(user))
        } catch (e) {
          // eslint-disable-next-line
          console.log('Error', e)
          commit(USER_UPDATE, null)
          commit(TOKENS_CLEAR)
          commit(RECOVER_TOKEN_UPDATE, null)
          commit(FORGOT_PASSWORD_TOKEN_UPDATE, null)
        }
      }
    },
    async restoreDetails ({ state, commit }) {
      if (state.context != null && state.context.scope != null) {
        try {
          const { data } = await PASSPORT.get(`/api/v1/security/domains/n/${state.context.scope}/details`)
          commit(DETAILS_UPDATE, Object.freeze(data))
        } catch (e) {
          // eslint-disable-next-line
          console.log('Error', e)
          commit(DETAILS_UPDATE, null)
        }
      }
    },
    async signup ({ state, commit }, { login, password, recaptchaToken, roistatVisitId }) {
      try {
        const authContext = state.context
        const { data } = await PASSPORT.post('/api/v1/security/sign-up', {
          login,
          password,
          clientId: state.context.client.clientId,
          roistatVisitId,
          refcode: authContext.refcode,
          state: JSON.stringify({
            clientId: authContext.client.clientId,
            redirectUrl: authContext.redirectUrl,
            state: authContext.state,
            codeChallenge: authContext.codeChallenge
          })
        }, {
          headers: {
            ...omitBy({
              'x-recaptcha-token': recaptchaToken
            }, isNil)
          }
        })
        const { user, token, refreshToken } = data
        commit(USER_UPDATE, Object.freeze(user))
        commit(TOKENS_UPDATE, {
          token,
          refreshToken
        })
        commit(RECOVER_TOKEN_UPDATE, null)
        commit(FORGOT_PASSWORD_TOKEN_UPDATE, null)
        return data
      } catch (e) {
        handleError(e)
      }
    },
    async forgot ({ state, commit }, { login }) {
      try {
        const authContext = state.context
        const clientId = state.context.client.clientId
        const { data } = await PASSPORT.post('/api/v1/security/forgot', {
          clientId,
          login,
          state: JSON.stringify({
            clientId: authContext.client.clientId,
            redirectUrl: authContext.redirectUrl,
            state: authContext.state,
            codeChallenge: authContext.codeChallenge
          })
        })
        const { recoverToken } = data
        commit(USER_UPDATE, null)
        commit(TOKENS_CLEAR)
        commit(RECOVER_TOKEN_UPDATE, recoverToken)
      } catch (e) {
        handleError(e)
      }
    },
    async passwd ({ state }, { password }) {
      try {
        await PASSPORT.post('/api/v1/security/forgot/password/change', {
          password
        }, withAuthorization(state.forgotPasswordToken))
        // const { user, token } = data
        // // TODO @ipavlenko: Fetch extra data using user id and token
        // commit(USER_UPDATE, Object.freeze(user))
        // commit(TOKENS_UPDATE, token)
        // commit(RECOVER_TOKEN_UPDATE, null)
      } catch (e) {
        handleError(e)
      }
    },
    async signin ({ commit }, { login, password, recaptchaToken }) {
      try {
        const { data } = await PASSPORT.post('/api/v1/security/sign-in', {
          login,
          password
        }, {
          headers: {
            ...omitBy({
              'x-recaptcha-token': recaptchaToken
            }, isNil)
          }
        })
        const { user, token, refreshToken } = data
        commit(USER_UPDATE, Object.freeze(user))
        commit(TOKENS_UPDATE, {
          token,
          refreshToken
        })
        commit(RECOVER_TOKEN_UPDATE, null)
      } catch (e) {
        handleError(e)
      }
    },
    async signout ({ commit }) {
      commit(USER_UPDATE, null)
      commit(TOKENS_CLEAR)
      commit(RECOVER_TOKEN_UPDATE, null)
    },
    async confirmEmailByCode ({ commit, dispatch }, { code }) {
      try {
        const accessToken = await dispatch('requireToken')
        const { data } = await PASSPORT.post('/api/v1/security/confirm/email/code', { code }, withAuthorization(accessToken))
        const { user, token, refreshToken  } = data
        commit(USER_UPDATE, Object.freeze(user))
        commit(TOKENS_UPDATE, {
          token,
          refreshToken
        })
        commit(RECOVER_TOKEN_UPDATE, null)
      } catch (e) {
        handleError(e)
      }
    },
    async confirmSecret ({ commit, dispatch }, { secret }) {
      const { data } = await PASSPORT.post('/api/v1/security/confirm/secret', { secret })
      const { user, context, token, refreshToken } = data
      await dispatch('context', context)
      commit(USER_UPDATE, Object.freeze(user))
      commit(TOKENS_UPDATE, {
        token,
        refreshToken
      })
      commit(RECOVER_TOKEN_UPDATE, null)
    },
    async confirmEmailBySecret ({ commit, dispatch }, { secret }) {
      const { data } = await PASSPORT.post('/api/v1/security/confirm/email/secret', { secret })
      const { user, context, token, refreshToken } = data
      // see signup
      const savedContext = JSON.parse(context.state)
      const { clientId, redirectUrl, state, codeChallenge } = savedContext
      const client = await dispatch('fetchClient', { clientId })
      await dispatch('saveContext', { client, redirectUrl, state, codeChallenge })
      commit(USER_UPDATE, Object.freeze(user))
      commit(TOKENS_UPDATE, {
        token,
        refreshToken
      })
      commit(RECOVER_TOKEN_UPDATE, null)
      commit(FORGOT_PASSWORD_TOKEN_UPDATE, null)
    },
    async confirmRecoverEmailPasswordByCode ({ state, commit }, { code, recaptchaToken }) {
      try {
        const { data } = await PASSPORT.post('/api/v1/security/recover/email/code', {
          code
        }, withAuthorization(state.recoverToken, {
          headers: {
            ...omitBy({
              'x-recaptcha-token': recaptchaToken
            }, isNil)
          }
        }))
        const { user, token } = data
        commit(USER_UPDATE, Object.freeze(user))
        commit(FORGOT_PASSWORD_TOKEN_UPDATE, token)
      } catch (e) {
        handleError(e)
      }
    },
    async confirmRecoverEmailPasswordBySecret ({ commit, dispatch }, { secret }) {
      const { data } = await PASSPORT.post('/api/v1/security/recover/email/secret', { secret })
      const { user, context, token } = data
      // see forgot
      const savedContext = JSON.parse(context.state)
      const { clientId, redirectUrl, state, codeChallenge } = savedContext
      const client = await dispatch('fetchClient', { clientId })
      await dispatch('saveContext', { client, redirectUrl, state, codeChallenge })
      commit(USER_UPDATE, Object.freeze(user))
      commit(FORGOT_PASSWORD_TOKEN_UPDATE, token)
    },
    async grant ({ state, dispatch }, { code: verificationCode, grants, codeChallenge }) {
      try {
        const token = await dispatch('requireToken')
        const { data } = await PASSPORT.post('/api/v1/auth/grant/code', {
          grants,
          codeChallenge,
          clientId: state.context.client.clientId,
          redirectUrl: state.context.redirectUrl
        }, withAuthorizationAndVerification(token, verificationCode))
        const { code } = data
        return { code }
      } catch (e) {
        handleError(e)
      }
    },
    async fetchMyGrants ({ state, dispatch }) {
      const token = await dispatch('requireToken')
      const { data } = await PASSPORT.post('/api/v1/security/me/grants/query', {
        clientIds: [state.context.client.clientId]
      }, withAuthorization(token))

      return data.grants
    },
    async fetchAvailableScopes ({ state }) {
      const { data } = await PASSPORT.get(`/api/v1/auth/client/${state.context.client.clientId}/scopes`)
      const clientScopes = data.map(e => ClientScopeModel.fromJs(e))
      return clientScopes
    },
    async session ({ commit }, { /*authenticator,*/ token, refreshToken, user }) {
      commit(USER_UPDATE, Object.freeze(user))
      commit(TOKENS_UPDATE, {
        token,
        refreshToken
      })
      commit(RECOVER_TOKEN_UPDATE, null)
    },
    async enable2FA ({ dispatch, commit }, { secret, code }) {
      const request = {
        secret,
        otp: code
      }
      const accessToken = await dispatch('requireToken')
      const { data } = await PASSPORT.post('/api/v1/security/me/verificators/app', request, withAuthorization(accessToken))
      const { user, token, refreshToken } = data
      commit(USER_UPDATE, Object.freeze(user))
      commit(TOKENS_UPDATE, {
        token,
        refreshToken
      })
      return user
    },
    async disable2FA ({ dispatch, commit }, { code }) {
      const accessToken = await dispatch('requireToken')
      const { data } = await PASSPORT.delete('/api/v1/security/me/verificators', withAuthorizationAndVerification(accessToken, code))
      const { user, token, refreshToken } = data
      commit(USER_UPDATE, Object.freeze(user))
      commit(TOKENS_UPDATE, {
        token,
        refreshToken
      })
      return user
    },
    async requireToken ({ dispatch, commit }) {
      return requireTokenLock.acquire(
        LOCK_REQUIRE_TOKEN,
        async () => {
          const token = await dispatch('accessToken')
          if (token == null) {
            // eslint-disable-next-line
            console.info('[passport] No token found.')

            commit(USER_UPDATE, null)
            commit(TOKENS_CLEAR)
            commit(RECOVER_TOKEN_UPDATE, null)

            await dispatch('navigation/push', {
              path: '/security/signin'
            }, { root: true })
            throw new Error('No token found')
          }
          return token
        }
      )
    },
    async accessToken ({ state, commit, dispatch }) {
      try {
        assert(state.token != null, 'No token provided')
        assert(state.refreshToken != null, 'No refreshToken provided')

        const expirationTime = await dispatch('extractExpirationTime', { token: state.token })

        const now = moment()

        const diff = moment.unix(expirationTime).diff(now, 'seconds')

        if (diff > MIN_SECONDS_BEFORE_REFRESH) {
          if (diff <= MIN_SECONDS_BEFORE_WARN) {
            // eslint-disable-next-line
            console.info(`[passport] Token will expire in ${diff} second(s)`)
          }
          return state.token
        } else {
          // eslint-disable-next-line
          console.info('[passport] Token has expired. Attempt to refresh token.')
        }

        const { data } = await PASSPORT.post('/api/v1/security/refresh', null, withAuthorization(state.refreshToken))

        const {
          token,
          refreshToken
        } = data

        commit(TOKENS_UPDATE, {
          token,
          refreshToken
        })

        // eslint-disable-next-line
        console.info(`[passport] Token has been refreshed.`)

        return token
      } catch (e) {
        // eslint-disable-next-line
        console.info('[passport] No token found.', e)

        return null
      }
    },
    async extractExpirationTime ({ rootGetters }, { token }) {
      const pubKey = rootGetters['config/config'].jwt.pubKey
      if (token == null || pubKey == null || PASSPORT_CLIENT == null) {
        return null
      }
      try {
        const { expirationTime } = await PASSPORT_CLIENT.decodeAndVerifyToken({
          token,
          pubKey
        })
        return expirationTime
      } catch (e) {
        if (e instanceof TokenExpiredError) {
          return null
        }
      }
    }
  }
})

function handleError (error) {
  if (error.response) {
    const response = error.response
    let message
    let errorCode = ErrorCodes.UNKNOWN_ERROR

    if (response.data && response.data.error) {
      message = response.data.error.message
      errorCode = response.data.error.code
    } else if (typeof response.data === 'string') {
      message = response.data
    } else {
      errorCode = ErrorCodes.UNKNOWN_ERROR
    }

    throw new ServerResponseError({
      message,
      httpStatus: response.status,
      errorCode
    })
  }

  throw error
}
